import { useEffect, useState, useRef } from "react";
import api from "../../api/api";
import "./CourseTimeEstimator.css";

/* ── helpers ─────────────────────────────────────────────── */
const fmtMins = (mins) => {
  if (!mins || mins <= 0) return "0m";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

const PRESET_DAYS = [7, 14, 30, 60, 90];

/* ── Component ───────────────────────────────────────────── */
export default function CourseTimeEstimator({ course, progressPercent }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("cte_open") !== "false"; } catch { return true; }
  });
  const [targetDays,   setTargetDays]   = useState(30);
  const [custom,       setCustom]       = useState("");
  const [avgDailyMins, setAvgDailyMins] = useState(null);
  const [fetchedPace,  setFetchedPace]  = useState(false); // only fetch when opened
  const inputRef = useRef(null);

  // Fetch average pace lazily — only when panel is first opened
  useEffect(() => {
    if (!open || fetchedPace) return;
    setFetchedPace(true);
    api.get("/analytics/focus-trend?days=14")
      .then(res => {
        const days = res.data || [];
        const nonZero = days.filter(d => (d.minutes || d.totalMinutes || 0) > 0);
        if (!nonZero.length) { setAvgDailyMins(0); return; }
        const total = nonZero.reduce((sum, d) => sum + (d.minutes || d.totalMinutes || 0), 0);
        setAvgDailyMins(Math.round(total / nonZero.length));
      })
      .catch(() => setAvgDailyMins(0));
  }, [open, fetchedPace]);

  const toggleOpen = () => {
    setOpen(prev => {
      try { localStorage.setItem("cte_open", String(!prev)); } catch {}
      return !prev;
    });
  };

  if (!course) return null;

  // ── Core calculations ──────────────────────────────────
  const totalSecs     = course.totalDurationSec || 0;
  const totalMins     = totalSecs / 60;
  const doneFraction  = (progressPercent || 0) / 100;
  const remainingMins = Math.max(0, totalMins * (1 - doneFraction));

  // Daily requirement for target days
  const activeDays    = targetDays > 0 ? targetDays : 1;
  const dailyRequired = remainingMins / activeDays;

  // Estimated finish days at current pace
  const estDays = avgDailyMins > 0
    ? Math.ceil(remainingMins / avgDailyMins)
    : null;

  // Warning thresholds
  const isHeavy = dailyRequired > 180; // >3hrs/day
  const isLight = dailyRequired > 0 && dailyRequired < 10;

  // Bar fill — 120 mins/day = 100%
  const dayFill = Math.min((dailyRequired / 120) * 100, 100);

  const handlePreset = (d) => { setTargetDays(d); setCustom(""); };

  const handleCustomChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    setCustom(val);
    const n = parseInt(val);
    if (n > 0 && n <= 3650) setTargetDays(n);
  };

  const isCustomActive = custom !== "" && parseInt(custom) > 0;

  return (
    <div className="cte-wrap">

      {/* Header — always visible */}
      <button className="cte-header" onClick={toggleOpen} aria-expanded={open}>
        <span className="cte-header-left">
          <span className="cte-icon">📅</span>
          <span className="cte-label">Study Planner</span>
          {!open && remainingMins > 0 && (
            <span className="cte-preview">
              {fmtMins(remainingMins)} left
              {estDays ? ` · ~${estDays}d at your pace` : ""}
            </span>
          )}
        </span>
        <span className={`cte-chevron ${open ? "cte-chevron--up" : ""}`}>›</span>
      </button>

      {/* Body — collapsible */}
      {open && (
        <div className="cte-body">

          {/* Remaining time */}
          <div className="cte-remaining">
            <span className="cte-remaining-num">{fmtMins(remainingMins)}</span>
            <span className="cte-remaining-label">
              remaining
              {totalSecs > 0 && (
                <span className="cte-total"> of {fmtMins(totalMins)} total</span>
              )}
            </span>
          </div>

          {/* Day selector */}
          <div className="cte-section">
            <p className="cte-section-label">Complete in how many days?</p>
            <div className="cte-presets">
              {PRESET_DAYS.map(d => (
                <button
                  key={d}
                  className={`cte-preset ${targetDays === d && !isCustomActive ? "cte-preset--active" : ""}`}
                  onClick={() => handlePreset(d)}
                >
                  {d}d
                </button>
              ))}
              <input
                ref={inputRef}
                className={`cte-custom-input ${isCustomActive ? "cte-custom-input--active" : ""}`}
                type="text"
                inputMode="numeric"
                placeholder="Custom"
                value={custom}
                onChange={handleCustomChange}
                maxLength={4}
              />
            </div>
          </div>

          {/* Result */}
          {remainingMins > 0 ? (
            <div className={`cte-result ${isHeavy ? "cte-result--heavy" : isLight ? "cte-result--light" : ""}`}>
              <div className="cte-result-main">
                <span className="cte-result-num">{fmtMins(dailyRequired)}</span>
                <span className="cte-result-text">/ day for {activeDays} day{activeDays !== 1 ? "s" : ""}</span>
              </div>

              <div>
                <div className="cte-bar-track">
                  <div className="cte-bar-fill" style={{ width: `${dayFill}%` }} />
                </div>
                <span className="cte-bar-label">
                  {isHeavy
                    ? `⚠ Heavy load — try ${Math.ceil(remainingMins / 60)}+ days for under 1h/day`
                    : isLight
                    ? "✓ Very easy — just stay consistent"
                    : "✓ Achievable with daily sessions"}
                </span>
              </div>

              <p className="cte-streak-note">
                {isHeavy
                  ? `That's ${fmtMins(dailyRequired)}/day — consider spreading it over more days.`
                  : `${fmtMins(dailyRequired)}/day fits comfortably in a focused study session.`}
              </p>
            </div>
          ) : progressPercent >= 100 ? (
            <div className="cte-complete">🎉 Course complete!</div>
          ) : null}

          {/* At current pace */}
          <div className="cte-pace">
            <span className="cte-pace-icon">📊</span>
            <div className="cte-pace-text">
              {avgDailyMins === null ? (
                <span className="cte-pace-loading">Calculating your pace…</span>
              ) : avgDailyMins === 0 ? (
                <span className="cte-pace-none">No watch history yet — start watching to see your pace estimate.</span>
              ) : (
                <>
                  <span className="cte-pace-main">
                    At your pace ({fmtMins(avgDailyMins)}/day avg) →{" "}
                    <strong>
                      {estDays === 1 ? "finish today 🎯" : `~${estDays} day${estDays !== 1 ? "s" : ""}`}
                    </strong>
                  </span>
                  <span className="cte-pace-sub">Based on your last 14 days of watch activity</span>
                </>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}